import React from 'react';
import { Award, Heart, Wrench, Sparkles } from 'lucide-react';

export const About = () => {
  const services = [
    {
      icon: Wrench,
      title: 'Furniture Restoration & Refinishing',
      description: 'Expert restoration of antique and vintage furniture, bringing pieces back to their original beauty with meticulous attention to detail.'
    },
    {
      icon: Sparkles,
      title: 'Custom Furniture Design & Build',
      description: 'Bespoke furniture designed and crafted to your exact specifications, combining traditional techniques with modern aesthetics.'
    },
    {
      icon: Heart,
      title: 'Upholstery Services',
      description: 'Professional reupholstering using premium fabrics and materials, transforming worn pieces into stunning focal points.'
    },
    {
      icon: Award,
      title: 'Antique Restoration',
      description: 'Specialized care for valuable antiques, preserving historical integrity while ensuring structural soundness and beauty.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[500px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1687180498602-5a1046defaa4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBmdXJuaXR1cmUlMjBzaG93cm9vbXxlbnwwfHx8fDE3NzA4MTk3MzZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury Furniture Showroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-800 mb-4">
              About Us
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Crafting excellence through generations of expertise
            </p>
          </div>
        </div>
      </div>

      {/* Story Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-serif font-bold text-gray-800 mb-6">
              Our Story
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                For the past <span className="font-semibold text-gray-800">8 years</span>, Restoration by Design has been Mississauga's trusted name in furniture restoration and custom design. But our story goes much deeper than that.
              </p>
              <p>
                Our founder brings over <span className="font-semibold text-gray-800">23 years of experience</span> to every project, having learned the art of furniture restoration from parents who ran a similar business. This multi-generational expertise means we understand not just the techniques, but the soul of fine furniture.
              </p>
              <p>
                As a <span className="font-semibold text-gray-800">fully Canadian owned and operated business</span>, we take pride in supporting local craftmanship and maintaining the highest standards of quality. Every piece that comes through our workshop receives the same level of care and attention we'd give our own family heirlooms.
              </p>
              <p>
                Whether you're looking to restore a cherished antique, refresh a beloved piece, or create something entirely new, we bring decades of experience and a genuine passion for the craft to every project.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img
              src="https://images.unsplash.com/photo-1647426994723-ab685e4b8c77"
              alt="Furniture Restoration Work"
              className="w-full h-64 object-cover rounded-lg shadow-md"
            />
            <img
              src="https://images.unsplash.com/photo-1633419946251-6d8b5dd33170"
              alt="Workshop Tools"
              className="w-full h-64 object-cover rounded-lg shadow-md mt-8"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-rose-50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-gray-800 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive furniture solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-14 h-14 bg-rose-100 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="h-7 w-7 text-rose-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-12 text-center">
          <div>
            <div className="text-5xl font-serif font-bold text-rose-400 mb-3">23+</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">Years Experience</div>
            <p className="text-gray-600">Generational expertise passed down through family</p>
          </div>
          <div>
            <div className="text-5xl font-serif font-bold text-rose-400 mb-3">100%</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">Canadian Owned</div>
            <p className="text-gray-600">Proudly supporting local craftsmanship</p>
          </div>
          <div>
            <div className="text-5xl font-serif font-bold text-rose-400 mb-3">8</div>
            <div className="text-xl font-semibold text-gray-800 mb-2">Years in Business</div>
            <p className="text-gray-600">Trusted by Mississauga community</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-rose-50 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4">
            Ready to Transform Your Furniture?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can bring your vision to life or restore your cherished pieces.
          </p>
          <a
            href="/contact"
            className="inline-block bg-rose-400 text-white px-8 py-4 rounded-lg font-semibold hover:bg-rose-500 transition-colors shadow-sm"
          >
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
};
